import React, { forwardRef } from 'react';
import { Box, Chip, type ChipProps, type SxProps, type Theme } from '@mui/material';

import { StyledContainer } from './CompoundChip.styles';

export interface CompoundChipProps extends Omit<ChipProps, 'ref'> {
  /** Secondary element (icon, avatar, etc.) displayed alongside the primary chip */
  secondary?: React.ReactNode;
  /** Which side the secondary element appears on. @default 'end' */
  secondaryPlacement?: 'start' | 'end';
  /** Background color of the outer container. @default theme.palette.action.selected */
  containerColor?: string;
  /** sx override for the outer container */
  containerSx?: SxProps<Theme>;
}

/**
 * Compound chip primitive — a primary Chip with an optional secondary element
 * (icon, avatar, etc.) displayed alongside it inside a shared pill-shaped container.
 *
 * All standard MUI ChipProps are forwarded to the inner Chip.
 *
 * @example
 * <CompoundChip
 *   label="Published"
 *   color="success"
 *   icon={<PublishedIcon />}
 *   secondary={<EditIcon />}
 * />
 */
export const CompoundChip = forwardRef<HTMLDivElement, CompoundChipProps>(
  (
    {
      secondary,
      secondaryPlacement = 'end',
      containerColor,
      containerSx,
      ...chipProps
    },
    ref,
  ) => {
    // When there's no secondary element, render a plain Chip
    if (!secondary) {
      return <Chip ref={ref} {...chipProps} />;
    }

    return (
      <StyledContainer ref={ref} containerColor={containerColor} sx={containerSx}>
        {secondaryPlacement === 'start' && (
          <Box className="CompoundChip-secondary CompoundChip-secondary-start">
            {secondary}
          </Box>
        )}
        <Chip {...chipProps} />
        {secondaryPlacement === 'end' && (
          <Box className="CompoundChip-secondary CompoundChip-secondary-end">
            {secondary}
          </Box>
        )}
      </StyledContainer>
    );
  },
);

CompoundChip.displayName = 'CompoundChip';
